import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';
import { AppUser, BrokerConnection } from '../../types/user';

const Settings: React.FC = () => {
  const { currentUser } = useAuth();
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [connections, setConnections] = useState<BrokerConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tradier');

  // Tradier form
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');


  useEffect(() => {
    Promise.all([userService.getMe(), userService.getBrokerConnections()])
      .then(([user, conns]) => {
        setAppUser(user);
        setConnections(conns);
      })
      .finally(() => setLoading(false));
  }, []);

  const tradierConn = connections.find((c) => c.broker === 'tradier') ?? null;

  const handleSaveTradier = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');
    setSaveSuccess('');
    if (!apiKey.trim()) {
      setSaveError('API key is required');
      return;
    }
    setSaving(true);
    try {
      const conn = await userService.saveBrokerConnection({
        broker: 'tradier',
        credentials: { api_key: apiKey.trim() },
      });
      setConnections((prev) => {
        const others = prev.filter((c) => c.broker !== 'tradier');
        return [...others, conn];
      });
      setApiKey('');
      setSaveSuccess('Tradier connection saved and validated.');
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Settings</Typography>

      {/* Account */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Account</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>Email:</strong> {currentUser?.email}
            </Typography>
            <Typography variant="body2">
              <strong>Display name:</strong> {appUser?.display_name ?? '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Role:</strong>{' '}
              <Chip
                label={appUser?.role ?? 'trader'}
                size="small"
                color={appUser?.role === 'admin' ? 'secondary' : 'default'}
              />
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Broker integration */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Broker Integration</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Credentials are encrypted before storage and never returned to the client.
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Tradier" value="tradier" />
          </Tabs>

          {activeTab === 'tradier' && (
            <Box>
              {/* Connection status */}
              {tradierConn && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  {tradierConn.is_valid
                    ? <CheckCircleIcon color="success" fontSize="small" />
                    : <ErrorIcon color="error" fontSize="small" />
                  }
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {tradierConn.is_valid ? 'Connected' : 'Invalid key'}
                    </Typography>
                    {tradierConn.account_id && (
                      <Typography variant="caption" color="text.secondary">
                        Account: {tradierConn.account_id}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Typography variant="body2" fontWeight={600} gutterBottom>
                {tradierConn ? 'Update API Key' : 'Connect Tradier'}
              </Typography>

              <Box component="form" onSubmit={handleSaveTradier} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="API Key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your Tradier API key"
                  size="small"
                  required
                  fullWidth
                  helperText="tradier.com → Account → API Access"
                />

                {saveError && <Alert severity="error">{saveError}</Alert>}
                {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}

                <Button type="submit" variant="contained" disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                  {saving ? 'Saving…' : tradierConn ? 'Update Key' : 'Connect'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {appUser?.role === 'admin' && (
        <Box mt={2}>
          <Button variant="outlined" href="/admin/tickers" size="small" sx={{ mr: 1 }}>
            Ticker Watchlist →
          </Button>
          <Button variant="outlined" href="/admin/jobs" size="small">
            Job Triggers →
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default Settings;
